import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Ban, Copy, RefreshCw, Send } from 'lucide-react';
import { toast } from "react-toastify";
import useApi from "@src/api_service/useApi";
import LoadingStateComp from "@src/components/LoadingComponent/LoadingComponent";
import { cn } from "@src/utils/app_functions";
import { ApiErrorBody, LinkTransaction, SchoolPaymentLink, SchoolPaymentLinkPlan } from "./interface";
import {
  EVENT_DOT,
  EVENT_LABELS,
  StatusPill,
  adminName,
  authHeaders,
  copyToClipboard,
  errorMessage,
  formatDate,
  formatMoney,
  isOnboardingLink,
  LINK_TYPE_LABELS,
  linkPaysFor,
} from "./utils";
import { CancelLinkModal, useLinkActions } from "./components/LinkActions";

type LayoutContextType = {
  setSchoolAdminPageName: React.Dispatch<React.SetStateAction<string>>;
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>;
  setMenu: React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>;
  setIsAffiliate: React.Dispatch<React.SetStateAction<boolean>>;
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 break-words">{children}</dd>
  </div>
);

const TX_STATUS_STYLES: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  successful: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-700',
};

const SchoolPaymentLinkDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const path = window.location.pathname;
  const { setSchoolAdminPageName, setShowInput, setMenu, setIsAffiliate } = useOutletContext<LayoutContextType>();

  const [link, setLink] = useState<SchoolPaymentLink | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const { loading, get } = useApi();

  useEffect(() => {
    setSchoolAdminPageName("Payment Link");
    setShowInput(false);
    setMenu(undefined);
    setIsAffiliate(false);
  }, [path]);

  const fetchLink = useCallback(async (showLoading = true) => {
    if (!id) return;
    await get<{ data: SchoolPaymentLink }, ApiErrorBody>(
      `/third_party/school_payment_links/${id}`,
      authHeaders(false),
      {
        loading: showLoading,
        onSuccess: (res) => {
          setLink(res.data);
          setNotFound(false);
        },
        onError: (err) => {
          setNotFound(true);
          toast.error(errorMessage(err, 'Could not load the payment link'));
        },
      }
    );
  }, [get, id]);

  useEffect(() => {
    fetchLink();
  }, [fetchLink]);

  const actions = useLinkActions(() => fetchLink(false));

  if (loading && !link) return <LoadingStateComp Message="Payment Link" />;

  if (!link) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 mb-4">{notFound ? 'Payment link not found.' : 'Loading...'}</p>
        <button onClick={() => navigate('/home/school-payment-links')} className="text-[#E55109] hover:underline">
          Back to payment links
        </button>
      </div>
    );
  }

  const plan = typeof link.plan === 'object' ? (link.plan as SchoolPaymentLinkPlan) : null;
  const transactions = link.transactions.filter((t): t is LinkTransaction => typeof t === 'object' && t !== null);
  const paidTxId = typeof link.paidTransaction === 'object' ? link.paidTransaction?._id : link.paidTransaction;
  // newest first, as an activity feed
  const events = [...(link.events ?? [])].reverse();

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <button
          onClick={() => navigate('/home/school-payment-links')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to payment links
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{link.organizationName || link.thirdPartyAppId}</h1>
                <StatusPill status={link.status} />
                <span
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium",
                    isOnboardingLink(link) ? "bg-indigo-100 text-indigo-800" : "bg-gray-100 text-gray-700"
                  )}
                >
                  {LINK_TYPE_LABELS[link.linkType ?? 'subscription']}
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatMoney(link.amount, link.currency)}</p>
              <p className={cn("text-gray-600", !isOnboardingLink(link) && "capitalize")}>{linkPaysFor(link)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => fetchLink(false)}
                title="Refresh"
                className="px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={async () => {
                  if (await copyToClipboard(link.url)) toast.success('Payment link copied');
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <Copy className="w-4 h-4" /> Copy link
              </button>
              {link.status === 'active' && (
                <>
                  <button
                    onClick={() => actions.resend(link)}
                    disabled={actions.loading}
                    className="flex items-center gap-2 px-4 py-2 bg-[#E55109] text-white rounded-lg text-sm hover:bg-[#d14808] disabled:opacity-60"
                  >
                    <Send className="w-4 h-4" /> Resend email
                  </button>
                  <button
                    onClick={() => setShowCancel(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50"
                  >
                    <Ban className="w-4 h-4" /> Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          {link.outsidePlanRange && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              The student count is outside this plan's range
              {plan ? ` of ${plan.minStudents} to ${plan.maxStudents ?? 'unlimited'}` : ''}.
            </div>
          )}
          {link.status === 'active' && !link.emailSentAt && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              The email has not been delivered yet. Resend it, or copy the link and share it directly.
            </div>
          )}
          {link.status === 'expired' && (
            <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700">
              This link has expired. Create a new link to let the school pay.
            </div>
          )}

          <dl className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6 pt-6 border-t border-gray-100">
            <Field label="App ID">{link.thirdPartyAppId}</Field>
            <Field label="Sent to">{link.recipientEmail}</Field>
            <Field label="Created">
              {formatDate(link.createdAt)}
              {adminName(link.createdBy) && <div className="text-gray-500">by {adminName(link.createdBy)}</div>}
            </Field>
            <Field label="Expires">{formatDate(link.expiresAt)}</Field>
            <Field label="Email sent">{formatDate(link.emailSentAt)}</Field>
            {link.paidAt && <Field label="Paid">{formatDate(link.paidAt)}</Field>}
            {link.cancelledAt && (
              <Field label="Cancelled">
                {formatDate(link.cancelledAt)}
                {adminName(link.cancelledBy) && <div className="text-gray-500">by {adminName(link.cancelledBy)}</div>}
              </Field>
            )}
            {link.cancelReason && <Field label="Cancel reason">{link.cancelReason}</Field>}
            {link.note && (
              <div className="col-span-2 md:col-span-4">
                <Field label="Note">{link.note}</Field>
              </div>
            )}
            <div className="col-span-2 md:col-span-4">
              <Field label="Email sent">
                {link.emailSubject || link.emailMessage ? (
                  <div className="space-y-1">
                    {link.emailSubject && <div className="font-medium">{link.emailSubject}</div>}
                    {link.emailMessage && (
                      <div className="whitespace-pre-wrap text-gray-700">
                        {/* **text** is bold in the email; show it the same way here. */}
                        {link.emailMessage.split(/(\*\*(?=\S)[^\n]*?\S\*\*)/g).map((part, index) =>
                          /^\*\*(?=\S)[^\n]*?\S\*\*$/.test(part) ? <strong key={index}>{part.slice(2, -2)}</strong> : part
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">Standard wording</span>
                )}
              </Field>
            </div>
            <div className="col-span-2 md:col-span-4">
              <Field label="Link">
                <span className="font-mono text-xs text-gray-600">{link.url}</span>
              </Field>
            </div>
          </dl>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Audit trail */}
          <div className="lg:col-span-3 bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Audit trail</h2>
            {events.length === 0 ? (
              <p className="text-sm text-gray-500">No events recorded.</p>
            ) : (
              <ol className="relative border-l border-gray-200 ml-2 space-y-5">
                {events.map((event, index) => (
                  <li key={`${event.action}-${event.at}-${index}`} className="ml-5">
                    <span className={cn("absolute -left-[6px] mt-1.5 w-3 h-3 rounded-full ring-4 ring-white", EVENT_DOT[event.action] ?? 'bg-gray-400')} />
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span className="font-medium text-gray-900 text-sm">{EVENT_LABELS[event.action] ?? event.action}</span>
                      <span className="text-xs text-gray-500">{formatDate(event.at)}</span>
                    </div>
                    {adminName(event.by) && <div className="text-xs text-gray-500">by {adminName(event.by)}</div>}
                    {event.note && <p className="text-sm text-gray-700 mt-1">{event.note}</p>}
                    {(event.ip || event.userAgent) && (
                      <p className="text-xs text-gray-400 mt-1 break-all">
                        {event.ip}{event.ip && event.userAgent ? ' · ' : ''}{event.userAgent}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Checkouts */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Checkouts</h2>
            <p className="text-xs text-gray-500 mb-4">Every open of the link starts a new checkout; only the completed one is marked successful.</p>
            {transactions.length === 0 ? (
              <p className="text-sm text-gray-500">The school hasn't opened the link yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {[...transactions].reverse().map((tx) => (
                  <li key={tx._id} className="py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-gray-700 break-all">{tx.paymentRef || tx._id}</span>
                      <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium capitalize", TX_STATUS_STYLES[tx.status?.toLowerCase() ?? ''] ?? 'bg-gray-100 text-gray-700')}>
                        {tx.status || 'unknown'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-2">
                      {tx.totalAmountPaid != null && <span>{formatMoney(tx.totalAmountPaid, tx.currencyType || link.currency)}</span>}
                      {tx.channelUsed && <span className="capitalize">{tx.channelUsed}</span>}
                      <span>{formatDate(tx.createdAt)}</span>
                      {paidTxId === tx._id && <span className="text-emerald-700 font-medium">Paid this link</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <CancelLinkModal
        link={showCancel ? link : null}
        loading={actions.loading}
        onClose={() => setShowCancel(false)}
        onConfirm={async (reason) => {
          await actions.cancel(link, reason);
          setShowCancel(false);
        }}
      />
    </div>
  );
};

export default SchoolPaymentLinkDetailsPage;
